<script setup>
import { ref, computed, watch, nextTick } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'
import { Eye, EyeOff, ZoomIn } from 'lucide-vue-next'
import { bbox } from '@turf/turf'
import ProjectTabs from '@/pages/Project/Components/ProjectTabs.vue'
import SelectedAreaChip from '@/pages/Project/Components/SelectedAreaChip.vue'
import MapSource from '@/components/map/MapSource.vue'
import MapLayer from '@/components/map/MapLayer.vue'
import { captureTileUrl } from '@/services/monitoringApi'
import { useCaptures, captureKindLabel, captureKey, groupByBucket } from '@/composables/useCaptures'
import { useSelectedArea } from '@/composables/useAreaSelection'
import { resolveFeatureGeometry } from '@/composables/useFeatureGeometry'
import { formatTime } from '@/utils/datetime'

const CAPTURE_Z_POSITION = 190

const route = useRoute()
const store = useStore()
const orgId = computed(() => route.params.org_id)
const projectId = computed(() => route.params.project_id)
const map = computed(() => store.getters['geo/getMap'])
const mapReady = computed(() => store.getters['geo/getMapReady'])

const { captures, buckets, isLoading } = useCaptures(orgId, projectId)
const selectedArea = useSelectedArea(projectId)

// The feed narrows to the selection; map layer toggles are driven by
// `captures`, so already-visible imagery survives selection changes.
const visibleBuckets = computed(() => {
  const sel = selectedArea.value?.code
  if (!sel) return buckets.value
  return groupByBucket(captures.value.filter((c) => c.feature_code === sel))
})

const visibleKeys = ref([])
const activeKey = ref(null)
const itemEls = ref({})

function layerIds(key) {
  const safe = key.replace(/[^a-zA-Z0-9_-]/g, '-')
  return { layerId: `monitoring-capture-${safe}`, sourceId: `monitoring-capture-src-${safe}` }
}

// Rendered declaratively through MapSource/MapLayer below — they own the
// add/remove/style-reload lifecycle that used to be hand-rolled here.
const mountedEntries = computed(() =>
  captures.value
    .filter((c) => c.status === 'ready' && visibleKeys.value.includes(captureKey(c)))
    .map((c) => ({ key: captureKey(c), ids: layerIds(captureKey(c)), url: captureTileUrl(orgId.value, c) }))
    .filter((e) => e.url)
)

function isVisible(capture) {
  return visibleKeys.value.includes(captureKey(capture))
}

function toggleCapture(capture) {
  const key = captureKey(capture)
  visibleKeys.value = visibleKeys.value.includes(key)
    ? visibleKeys.value.filter((k) => k !== key)
    : [...visibleKeys.value, key]
}

function setItemEl(key, el) {
  if (el) itemEls.value[key] = el
}

function captureDate(capture) {
  return formatTime(capture.item_date, null, 'D MMMM YYYY')
}

async function zoomToCapture(capture) {
  if (!map.value || !capture.feature_code) return
  const geometry = await resolveFeatureGeometry(capture.feature_code)
  if (!geometry) return
  try {
    map.value.fitBounds(bbox({ type: 'Feature', geometry }), { padding: 100, duration: 500 })
  } catch (error) {
    console.error('Error zooming to capture area:', error)
  }
}

let deepLinked = false
watch(captures, async (list) => {
  if (deepLinked || !route.query.capture || !list.length) return
  deepLinked = true
  const match = list
    .filter((c) => c.feature_code === route.query.capture && c.status === 'ready')
    .sort((a, b) => Date.parse(b.item_date) - Date.parse(a.item_date))[0]
  if (!match) return
  const key = captureKey(match)
  activeKey.value = key
  if (!visibleKeys.value.includes(key)) {
    visibleKeys.value = [...visibleKeys.value, key]
  }
  await nextTick()
  itemEls.value[key]?.scrollIntoView({ behavior: 'smooth', block: 'center' })
})
</script>

<template>
  <ProjectTabs />
  <SelectedAreaChip />

  <template v-if="map && mapReady">
    <template v-for="entry in mountedEntries" :key="entry.key">
      <MapSource
        :map="map"
        :id="entry.ids.sourceId"
        type="raster"
        :options="{ tiles: [entry.url], tileSize: 256 }"
      />
      <MapLayer
        :map="map"
        :id="entry.ids.layerId"
        type="raster"
        :source="entry.ids.sourceId"
        :before-id="`z-${CAPTURE_Z_POSITION}`"
        :interactive="false"
      />
    </template>
  </template>

  <div class="px-3 py-2 space-y-4">
    <div
      v-if="!visibleBuckets.length && !isLoading"
      class="rounded-lg border bg-card px-3 py-6 text-center text-sm text-muted-foreground"
    >
      No imagery yet — captures appear here as they are ordered
    </div>

    <section v-for="bucket in visibleBuckets" :key="bucket.label">
      <h2 class="text-sm font-semibold mb-2">{{ bucket.label }}</h2>
      <div class="space-y-2">
        <div
          v-for="capture in bucket.items"
          :key="captureKey(capture)"
          :ref="(el) => setItemEl(captureKey(capture), el)"
          class="rounded-lg border bg-card p-2.5"
          :class="activeKey === captureKey(capture) ? 'border-primary ring-1 ring-primary/30' : ''"
        >
          <div class="flex items-center gap-2.5">
            <div class="min-w-0 flex-1" :class="capture.status !== 'ready' ? 'opacity-60' : ''">
              <div class="text-sm font-semibold truncate">{{ captureKindLabel(capture.kind) }}</div>
              <div class="text-xs text-muted-foreground">{{ captureDate(capture) }}</div>
              <div v-if="capture.feature_name" class="mt-1 flex flex-wrap gap-1">
                <span class="rounded-full bg-muted px-1.5 py-0.5 text-[11px] font-medium">
                  {{ capture.feature_name }}
                </span>
              </div>
            </div>
            <button
              v-if="capture.feature_code"
              class="shrink-0 rounded-md border p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
              title="Zoom to area"
              @click="zoomToCapture(capture)"
            >
              <ZoomIn class="size-4" />
            </button>
            <button
              v-if="capture.status === 'ready'"
              class="shrink-0 rounded-md border p-1.5 transition-colors"
              :class="isVisible(capture) ? 'border-primary text-primary' : 'text-muted-foreground hover:bg-accent hover:text-foreground'"
              :title="isVisible(capture) ? 'Hide on map' : 'Show on map'"
              @click="toggleCapture(capture)"
            >
              <Eye v-if="isVisible(capture)" class="size-4" />
              <EyeOff v-else class="size-4" />
            </button>
            <span v-else class="shrink-0 text-xs text-muted-foreground">
              {{ capture.status === 'failed' ? 'Failed' : 'Processing…' }}
            </span>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>
